import React from "react";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Box from "@material-ui/core/Box";
import CloseIcon from "@material-ui/icons/Close";
import { IconButton } from "@material-ui/core";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import { FormErrors } from "../../components/Login/FormErrors";
import { userService } from "_services/user.service";
import ConfirmModal from "components/Modal/modal";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import { EMAIL_REGEX, FACILITY_ADMIN, DP_USER } from "__helpers/constants";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Slide,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
// Active facility = URL ?facilityId= (Org Admin new-tab) -> else own DP facility.
import { getActiveFacilityId } from "__helpers/activeFacility";

/**
 * Manage Users for a single facility (Facility/Org Admin only). Invite a DP or
 * Facility Admin, (de)activate, and promote/demote — scoped to the active facility
 * (URL ?facilityId= for an Org Admin tab, else the caller's own DP facility).
 * Restyled to the MAXMRJ theme; endpoints/payloads unchanged.
 */

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="right" ref={ref} {...props} />;
});

const headCells = [
  { id: "name", label: "Name" },
  { id: "email", label: "Email" },
  { id: "type", label: "Type" },
  { id: "status", label: "Status" },
  { id: "action", label: "Actions" },
];

function roleLabel(role) {
  return role === FACILITY_ADMIN ? "Facility Admin" : "Discharge Planner";
}

class ManageFacilityUsers extends React.Component {
  constructor(props) {
    super(props);
    let spinner = document.getElementById("loadingSpinner");
    this.state = {
      loading: false,
      spinner: spinner,
      facilityId: getActiveFacilityId(),
      users: [],
      search: "",
      // invite dialog
      modalStatus: false,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      role: DP_USER,
      formErrors: { firstName: "", lastName: "", email: "", phoneNumber: "" },
      firstNameValid: false,
      lastNameValid: false,
      emailValid: false,
      phoneNumberValid: false,
      // confirm (status toggle / role change)
      confirmModalStatus: false,
      confirmModalMessage: "",
      pendingAction: null, // { type: 'status' | 'role', user, newRole }
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.confirmModalSubmit = this.confirmModalSubmit.bind(this);
  }

  componentDidMount() {
    this.loadUsers();
  }

  loadUsers = () => {
    const { facilityId } = this.state;
    if (!facilityId) {
      this.setState({ users: [] });
      return;
    }
    if (this.state.spinner) this.state.spinner.removeAttribute("hidden", "true");
    fetch(`${apiPath.facilityAdminUsers}?facilityId=${facilityId}`, {
      method: "GET",
      headers: generalAuthTokenHeader(),
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => this.setState({ users: Array.isArray(data) ? data : [] }))
      .catch(() => this.setState({ users: [] }))
      .finally(() => {
        if (this.state.spinner) this.state.spinner.setAttribute("hidden", "true");
      });
  };

  // ----- invite dialog -----
  modalOpen = () => {
    this.setState({
      modalStatus: true,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      role: DP_USER,
      formErrors: { firstName: "", lastName: "", email: "", phoneNumber: "" },
      firstNameValid: false,
      lastNameValid: false,
      emailValid: false,
      phoneNumberValid: false,
    });
  };

  modalClose = () => {
    this.setState({ modalStatus: false });
  };

  handleChangeInput = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    this.setState({ [name]: value }, () => this.validateField(name, value));
  };

  // Role is a simple select; no validation needed.
  handleRoleChange = (event) => {
    this.setState({ role: event.target.value });
  };

  validateField = (fieldName, fieldValue) => {
    let fieldValidationErrors = this.state.formErrors;
    let firstNameValid = this.state.firstNameValid;
    let lastNameValid = this.state.lastNameValid;
    let emailValid = this.state.emailValid;
    let phoneNumberValid = this.state.phoneNumberValid;
    switch (fieldName) {
      case "firstName":
        firstNameValid = fieldValue.trim().length > 0;
        fieldValidationErrors.firstName = firstNameValid
          ? ""
          : "First name is required.";
        break;
      case "lastName":
        lastNameValid = fieldValue.trim().length > 0;
        fieldValidationErrors.lastName = lastNameValid
          ? ""
          : "Last name is required.";
        break;
      case "email":
        if (fieldValue.trim().length === 0) {
          emailValid = false;
          fieldValidationErrors.email = "Email is required.";
        } else if (!EMAIL_REGEX.test(fieldValue.trim())) {
          emailValid = false;
          fieldValidationErrors.email = "Please enter a valid email address.";
        } else {
          emailValid = true;
          fieldValidationErrors.email = "";
        }
        break;
      case "phoneNumber":
        if (fieldValue.trim().length === 0) {
          phoneNumberValid = false;
          fieldValidationErrors.phoneNumber = "Phone number is required.";
        } else if (!/^\d{6,15}$/.test(fieldValue.trim())) {
          phoneNumberValid = false;
          fieldValidationErrors.phoneNumber = "Please enter a valid phone number.";
        } else {
          phoneNumberValid = true;
          fieldValidationErrors.phoneNumber = "";
        }
        break;
      default:
        break;
    }
    this.setState({
      formErrors: fieldValidationErrors,
      firstNameValid,
      lastNameValid,
      emailValid,
      phoneNumberValid,
    });
  };

  validateForm() {
    return (
      this.state.firstNameValid &&
      this.state.lastNameValid &&
      this.state.emailValid &&
      this.state.phoneNumberValid
    );
  }

  handleSubmit(event) {
    event.preventDefault();
    this.invite();
  }

  async invite() {
    const { facilityId } = this.state;
    if (!facilityId) return;
    const payload = {
      firstName: this.state.firstName,
      lastName: this.state.lastName,
      email: this.state.email,
      phoneCode: "+1",
      phoneNumber: this.state.phoneNumber,
      twoFaPhoneCode: "+1",
      twoFaPhoneNumber: "",
      activated: true,
      facility_id: String(facilityId),
      imageUrl: "string",
      langKey: "en",
      login: "1",
    };
    this.setState({ loading: true });
    await fetch(
      `${apiPath.facilityAdminUsers}?role=${encodeURIComponent(this.state.role)}`,
      {
        method: "POST",
        headers: generalAuthTokenHeader(),
        body: JSON.stringify(payload),
      }
    )
      .then((response) => {
        this.setState({ loading: false });
        if (response.ok) {
          userService.showNotification({
            title: "Success",
            message: "User created and invited.",
            type: "success",
          });
          this.modalClose();
          this.loadUsers();
        } else {
          userService.showNotification({
            title: "Manage Users",
            message: "Failed to create user.",
            type: "danger",
          });
        }
      })
      .catch(() => {
        userService.showNotification({
          title: "Manage Users",
          message: "Failed to create user.",
          type: "danger",
        });
        this.setState({ loading: false });
      });
  }

  // ----- confirm (status toggle / role change) -----
  confirmStatus = (user) => {
    this.setState({
      pendingAction: { type: "status", user },
      confirmModalMessage: user.activated
        ? "Do you want to deactivate this user?"
        : "Do you want to activate this user?",
      confirmModalStatus: true,
    });
  };

  confirmRole = (user) => {
    const newRole = user.role === FACILITY_ADMIN ? DP_USER : FACILITY_ADMIN;
    this.setState({
      pendingAction: { type: "role", user, newRole },
      confirmModalMessage:
        newRole === FACILITY_ADMIN
          ? "Promote this user to Facility Admin?"
          : "Demote this user to Discharge Planner?",
      confirmModalStatus: true,
    });
  };

  confirmModalClose = () => {
    this.setState({ confirmModalStatus: false });
  };

  async confirmModalSubmit() {
    const { facilityId, pendingAction } = this.state;
    if (!pendingAction || !facilityId) return;
    const { type, user, newRole } = pendingAction;
    const url =
      type === "status"
        ? `${apiPath.facilityAdminUsers}/${user.userId}/status?facilityId=${facilityId}&activated=${!user.activated}`
        : `${apiPath.facilityAdminUsers}/${user.userId}/role?facilityId=${facilityId}&role=${encodeURIComponent(newRole)}`;
    this.setState({ loading: true });
    await fetch(url, { method: "PUT", headers: generalAuthTokenHeader() })
      .then((response) => {
        this.setState({ loading: false });
        this.confirmModalClose();
        if (response.ok) {
          userService.showNotification({
            title: "Success",
            message:
              type === "status"
                ? "Status updated successfully"
                : "Role updated successfully",
            type: "success",
          });
          this.loadUsers();
        } else {
          userService.showNotification({
            title: "Manage Users",
            message:
              type === "status"
                ? "Failed to change status."
                : "Failed to change role.",
            type: "danger",
          });
        }
      })
      .catch(() => {
        userService.showNotification({
          title: "Manage Users",
          message: "Failed to update user.",
          type: "danger",
        });
        this.setState({ loading: false });
        this.confirmModalClose();
      });
  }

  render() {
    const { facilityId } = this.state;
    const q = (this.state.search || "").trim().toLowerCase();
    const fullName = (u) => ((u.firstName || "") + " " + (u.lastName || "")).trim();
    const visibleUsers = this.state.users
      .filter((u) => {
        if (!q) return true;
        return fullName(u).toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
      })
      .sort((a, b) => fullName(a).toLowerCase().localeCompare(fullName(b).toLowerCase()));
    return (
      <div id="manage-facility-users-index">
        <ConfirmModal
          title="Alert"
          message={this.state.confirmModalMessage}
          confirmModalStatus={this.state.confirmModalStatus}
          confirmModalClose={this.confirmModalClose}
          submit={this.confirmModalSubmit}
          loading={this.state.loading}
        />

        {/* Invite user */}
        <Dialog
          open={this.state.modalStatus}
          maxWidth={"sm"}
          onClose={this.modalClose}
          TransitionComponent={Transition}
          aria-labelledby="form-dialog-title"
          className="add-modal"
        >
          <DialogTitle className="add-modal-title" id="form-dialog-title">
            Add User
            <IconButton
              className="closeButton"
              aria-label="close"
              onClick={this.modalClose}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <form onSubmit={this.handleSubmit} noValidate>
            <DialogContent>
              <Box display="flex" justifyContent="flex-start" mb={2} mt={2}>
                <Box width={1 / 2} pr={1}>
                  <TextField
                    InputLabelProps={{ className: "required-label" }}
                    name="firstName"
                    value={this.state.firstName}
                    onChange={this.handleChangeInput}
                    data-validators="isRequired"
                    label="First Name"
                  />
                  <FormErrors
                    show={!this.state.firstNameValid}
                    formErrors={this.state.formErrors}
                    fieldName="firstName"
                  />
                </Box>
                <Box width={1 / 2} pl={1}>
                  <TextField
                    InputLabelProps={{ className: "required-label" }}
                    name="lastName"
                    value={this.state.lastName}
                    onChange={this.handleChangeInput}
                    data-validators="isRequired"
                    label="Last Name"
                  />
                  <FormErrors
                    show={!this.state.lastNameValid}
                    formErrors={this.state.formErrors}
                    fieldName="lastName"
                  />
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-start" mb={2}>
                <Box width={1 / 2} pr={1}>
                  <TextField
                    InputLabelProps={{ className: "required-label" }}
                    name="email"
                    value={this.state.email}
                    onChange={this.handleChangeInput}
                    data-validators="isRequired"
                    label="Email"
                  />
                  <FormErrors
                    show={!this.state.emailValid}
                    formErrors={this.state.formErrors}
                    fieldName="email"
                  />
                </Box>
                <Box width={1 / 2} pl={1}>
                  <TextField
                    InputLabelProps={{ className: "required-label" }}
                    name="phoneNumber"
                    value={this.state.phoneNumber}
                    onChange={this.handleChangeInput}
                    data-validators="isRequired"
                    label="Phone Number"
                  />
                  <FormErrors
                    show={!this.state.phoneNumberValid}
                    formErrors={this.state.formErrors}
                    fieldName="phoneNumber"
                  />
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-start" mb={2}>
                <Box width={1 / 2} pr={1}>
                  <TextField
                    select
                    fullWidth
                    name="role"
                    value={this.state.role}
                    onChange={this.handleRoleChange}
                    label="Role"
                  >
                    <MenuItem value={DP_USER}>Discharge Planner</MenuItem>
                    <MenuItem value={FACILITY_ADMIN}>Facility Admin</MenuItem>
                  </TextField>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions className="modal-actions" justify="center">
              <Button
                type="submit"
                className="btn1"
                disabled={!this.validateForm() || this.state.loading}
              >
                {this.state.loading && (
                  <CircularProgress size={24} className="buttonProgress" />
                )}
                Add User
              </Button>
              <Button onClick={this.modalClose} className="btn2" color="primary">
                Cancel
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <GridContainer>
          <Card className={"advert-cover"}>
            <GridItem xs={12} sm={12} md={12}>
              <CardHeader
                color="success"
                className={"cusCardheader cusCardTitle"}
              >
                <h4>Manage Users</h4>
              </CardHeader>
            </GridItem>
            <div className={"custom-container"}>
              {facilityId ? (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                  <div className="search-btn-cover" style={{ float: "none" }}>
                    <input
                      type="text"
                      placeholder="Search by name or email."
                      className="search-btn"
                      value={this.state.search}
                      onChange={(e) => this.setState({ search: e.target.value })}
                    />
                    <i className="material-icons">search</i>
                  </div>
                  <div className="button-cover" style={{ float: "none" }}>
                    <button className="pointer btn1" onClick={this.modalOpen}>
                      Add User
                    </button>
                  </div>
                </div>
              ) : null}
              {facilityId ? (
                <div className="tableResponsive" style={{ clear: "both" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {headCells.map((headCell) => (
                          <TableCell key={headCell.id} align="left">
                            {headCell.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleUsers.length ? (
                        visibleUsers.map((u) => (
                          <TableRow key={u.userId}>
                            <TableCell>
                              {(u.firstName || "") + " " + (u.lastName || "")}
                            </TableCell>
                            <TableCell>{u.email}</TableCell>
                            <TableCell>{roleLabel(u.role)}</TableCell>
                            <TableCell>
                              {u.activated ? "Active" : "Inactive"}
                            </TableCell>
                            <TableCell>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "nowrap",
                                  gap: 8,
                                  alignItems: "center",
                                }}
                              >
                                <button
                                  type="button"
                                  className="pointer btn1"
                                  onClick={() => this.confirmRole(u)}
                                >
                                  {u.role === FACILITY_ADMIN
                                    ? "Demote"
                                    : "Promote"}
                                </button>
                                <button
                                  type="button"
                                  className="pointer btn1"
                                  onClick={() => this.confirmStatus(u)}
                                >
                                  {u.activated ? "Deactivate" : "Activate"}
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan="5">
                            <div className="not-found-text">No users found.</div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="not-found-text">
                  No facility context available for this user.
                </div>
              )}
            </div>
          </Card>
        </GridContainer>
      </div>
    );
  }
}

export default ManageFacilityUsers;
